import { OmniaApiChange } from "../api/types"
import { OmniaModuleId } from "../generated/enums"
import { OmniaPromise } from "../promises"
import { IOmniaSession } from "../types"

/**
* Base class for interfaces with the API
*
* This is a simplified version of the OmniaService class used in most Angular-based Omnia modules
*/
export abstract class ApiInterface<RefreshScope = never> {

    abstract startRefresh(scope: boolean | RefreshScope): OmniaPromise<any>

    protected _changeNumber = 0
    private _year = 0

    private _refresh: OmniaPromise<any>
    constructor(

        public readonly session: IOmniaSession,
        private readonly config: {
            name: string
            module: OmniaModuleId,
            dependencies: { refresh: () => ng.IPromise<any> }[]
        },
        private readonly moduleId = config?.module,
        public readonly http = session.http,
        protected readonly apiState = session.apiState
    ) {


    }

    /**
    * Checks if a refresh is required, and executes it if it this
    *
    * The actual refresh must be implemented by overriding the "startRefresh" method
    *
    * @param full If true, the refresh will always be executed. Otherwise, it will only
    * be executed if this seems to be required, mostly based on the events that were
    * recorded on this holding since the last refresh was executed
    */
    public async refresh(full: boolean = false) {

        const run = () => {

            const { session, _changeNumber, config } = this

            full = full || (this._year != session.yearId)

            const deps = config.dependencies.reduce((p, c) => {
                return p ? p.then(() => c.refresh()) : c.refresh()
            }, undefined as OmniaPromise<any>)

            const scope = full || (_changeNumber <= 0) || this.shouldRefresh(full)

            const local = scope
                ? () => this.startRefresh(scope)
                : undefined

            const refresh = local
                ? deps
                    ? deps.then(local)
                    : local()
                : deps

            if (!refresh) {
                return undefined
            }

            return refresh.then(_ => {

                this._refresh = undefined
                this._changeNumber = session.getApiVersion()
                this._year = session.yearId

            })

        }

        const p = this._refresh ?? (this._refresh = run())

        await p

    }

    async ensureInit() {

        if (this._refresh) {
            await this._refresh
        }

    }

    protected shouldRefresh(full: boolean): boolean | RefreshScope {
        if (full) return true
        if (!this._year) return true
        if (this._changeNumber <= 0) return true
        return (this.session.getApiVersion() > this._changeNumber)
    }

    getChanges(fromId: number, filter?: (change: OmniaApiChange) => boolean) {
        return this.apiState.getChanges(fromId, filter)
    }

    /**
      * Records a new local change
      * @param change Change
      * This can be used to force other services (specially AngularJS ones) to refresh their data
      */
    protected recordChange(change?: Omit<OmniaApiChange, "id" | "local">) {
        this.apiState.recordChange({
            ...change,
            local: true
        })
    }

}
